/**
 * WebSocketPoolProvider — React context provider for the WebSocketPool singleton.
 *
 * Responsibilities:
 * 1. Creates (via useMemo) and exposes the `WebSocketPool` singleton to the
 *    component tree through `WebSocketPoolContext`.
 * 2. Runs the `SessionBridge` — a set of pool event subscriptions that mirror
 *    pool state into the `activeSessionsStore` (Zustand) and TanStack Query.
 * 3. Tears down all connections on unmount via `pool.closeAll()`.
 *
 * Architecture note: the pool is intentionally zero-React. This provider is
 * the only place where pool events cross the boundary into React state, keeping
 * the coupling surface minimal and testable.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/components/AuthProvider';
import { WebSocketPool } from '../services/WebSocketPool';
import { USER_PROMPTS_QUERY_KEY } from '../hooks/useUserPrompts';
import {
  SKILLS_QUERY_KEY,
  skillsQueryKeys,
} from '@/features/agent-skills/useSkills';
import { useCabinetId } from '@/hooks/useCabinetMemberMe';
import { useActiveSessionsStore } from '../stores/activeSessionsStore';
import { appliquerLaListeDeLaConversation } from './conversationCasesBridge';
import { publierLeFilConfirme } from '@/features/redaction/state/canvasConversationGate';
import {
  handleConversationHistoryStale,
  handleSessionDoneSnapshot,
  handleSessionRemoved,
  handleTurnSettled,
  refreshPersonalizationAfterCaselessDockTurn,
} from './sessionDoneBridge';
import { notifyContextCompactionOutcome } from './contextCompactionFeedback';
import { invalidateCaseDocumentQueries } from './caseDocumentsMutationBridge';
import { invalidateCaseListQueries } from './caseListMutationBridge';

// ============================================================================
// Context
// ============================================================================

/**
 * React context holding the `WebSocketPool` singleton.
 * Components should access it via `useWebSocketPool()` — not the context directly.
 */
const WebSocketPoolContext = createContext<WebSocketPool | null>(null);

// ============================================================================
// Hook
// ============================================================================

/**
 * Returns the `WebSocketPool` singleton from the nearest `WebSocketPoolProvider`.
 *
 * @throws If called outside of a `WebSocketPoolProvider`.
 */
export function useWebSocketPool(): WebSocketPool {
  const pool = useContext(WebSocketPoolContext);
  if (!pool) {
    throw new Error(
      '[useWebSocketPool] Must be used inside a <WebSocketPoolProvider>.',
    );
  }
  return pool;
}

// ============================================================================
// SessionBridge — pool events → Zustand store + TanStack Query
// ============================================================================

/**
 * Subscribes to all pool events and writes the resulting state into
 * `activeSessionsStore` and TanStack Query.
 *
 * This is intentionally a hook (not a class) so it participates in the React
 * lifecycle and cleans up subscriptions automatically on unmount.
 *
 * Event mappings:
 * - `timeline_updated`      → store.setTimeline + pool.updateTimelineCache
 * - `status_changed`        → store.setSession (status + lastActivityAt)
 * - `connection_changed`    → store.setConnection
 * - `session_done`          → commit terminal snapshot locally + invalidate conversations list
 *                             + refresh Personnalisation settings when the case-less dock is open
 * - `conversation_confirmed`→ store.confirmConversationId
 * - `conversation_cases_changed` → liste des dossiers + avertissement de perte d'accès
 * - `memory_usage_updated`  → store.setSession (memoryUsage)
 * - `compact_context_capability_changed` → store.setSession (compactContextCapability)
 * - `context_compaction_changed` → store.setSession (compaction) + toast du verdict
 * - `conversation_history_stale` → transcript périmé + invalidation de l'historique
 *
 * Note on `reconnectFailed`: the `connection_changed` event carries
 * `(sessionKey, isConnected, reconnectInfo?)`. When `reconnectInfo` is absent
 * and `isConnected` is false, the event may mean either (a) the socket just
 * dropped, or (b) all reconnection attempts have been exhausted. Because both
 * cases are indistinguishable from the event alone, `reconnectFailed` is
 * preserved from the existing store state rather than overwritten — the pool
 * would need a dedicated event to communicate exhaustion explicitly.
 * This is tracked in DEBT-044.
 */
function useSessionBridge(pool: WebSocketPool): void {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const cabinetId = useCabinetId();
  const queryClient = useQueryClient();

  // Pull store actions once — these are stable references (Zustand guarantees).
  const setTimeline = useActiveSessionsStore((s) => s.setTimeline);
  const setSession = useActiveSessionsStore((s) => s.setSession);
  const setConnection = useActiveSessionsStore((s) => s.setConnection);
  const confirmConversationId = useActiveSessionsStore((s) => s.confirmConversationId);

  useEffect(() => {
    // ----- HMR orphan cleanup ------------------------------------------------
    // WHY: Vite HMR destroys the WebSocketPool singleton (and its WS connections)
    // but Zustand state survives the module reload. This leaves sessions marked
    // 'streaming' in the store with no pool connection backing them — the UI shows
    // infinite loading dots that can't be interrupted or reconnected.
    // On mount, clean up any session that exists in Zustand but not in the pool.
    const store = useActiveSessionsStore.getState();
    const orphanKeys = Object.keys(store.sessions).filter(
      (key) => !pool.getSessionStatus(key),
    );
    if (orphanKeys.length > 0) {
      console.warn(`[WS:bridge] Cleaning ${orphanKeys.length} orphaned sessions (likely HMR)`);
      for (const key of orphanKeys) {
        store.removeSession(key);
        store.removeConnection(key);
      }
    }

    // ----- timeline_updated ------------------------------------------------
    const unsubTimeline = pool.events.on(
      'timeline_updated',
      (sessionKey, timeline) => {
        setTimeline(sessionKey, timeline);
        // Write back into the pool cache so MessageHandler callbacks can read
        // the current timeline without depending on React state.
        pool.updateTimelineCache(sessionKey, timeline);
      },
    );

    // ----- status_changed --------------------------------------------------
    const unsubStatus = pool.events.on(
      'status_changed',
      (sessionKey, status) => {
        // turnStartedAt = origin of the feather timer, owned by the session (not
        // by StreamingTimeline local state) so it survives conversation switches
        // and remounts. Set when the turn becomes active ('streaming') if not
        // already set — `?? prev` preserves it across reconnect blips (which pass
        // through 'connecting' → 'streaming' again). Cleared when the turn ends.
        const prev = useActiveSessionsStore.getState().sessions[sessionKey]?.turnStartedAt ?? null;
        const turnStartedAt =
          status === 'streaming'
            ? (prev ?? Date.now())
            : status === 'ready' || status === 'error'
              ? null
              : prev;
        setSession(sessionKey, { status, lastActivityAt: Date.now(), turnStartedAt });
      },
    );

    // ----- connection_changed ----------------------------------------------
    const unsubConnection = pool.events.on(
      'connection_changed',
      (sessionKey, isConnected, reconnectInfo) => {
        // Preserve existing reconnectFailed — only set to true when the pool
        // explicitly signals exhaustion (connection_changed with false + no info
        // AND no existing reconnect in progress). This avoids a false-positive
        // flash between disconnect and the first reconnect attempt.
        const existing = useActiveSessionsStore.getState().connections[sessionKey];
        const wasReconnecting = existing?.reconnectInfo != null;
        setConnection(sessionKey, {
          isConnected,
          reconnectInfo: reconnectInfo ?? null,
          reconnectFailed: isConnected ? false
            : reconnectInfo ? false
            : wasReconnecting ? true   // was reconnecting, now no info → exhausted
            : (existing?.reconnectFailed ?? false), // preserve existing state
        });
      },
    );

    // ----- session_done ----------------------------------------------------
    const unsubDone = pool.events.on(
      'session_done',
      (snapshot) => {
        handleSessionDoneSnapshot(snapshot, queryClient);
        // Turn-scoped refresh of the Personnalisation settings — deliberately NOT
        // tool-scoped. The rationale, and the backend-event upgrade path it stands
        // in for, are written out at the definition site.
        refreshPersonalizationAfterCaselessDockTurn(queryClient, cabinetId);
        // WHY no deferred cleanup: the session stays alive (status: ready) so the
        // user can continue the conversation. Cleanup happens on slot eviction or
        // when the backend sends session_expired after 30min inactivity.
      },
    );

    // ----- conversation_confirmed ------------------------------------------
    // Fires when the backend confirms the conversation row exists in DB
    // (via conversation_created event after first user_message).
    const unsubConversation = pool.events.on(
      'conversation_confirmed',
      (sessionKey, conversationId) => {
        confirmConversationId(sessionKey, conversationId);
        // La même preuve, publiée pour les lecteurs HORS React (#1331) : la
        // grille d'un dossier peut ouvrir une pièce dans le Canvas sans que
        // l'écran assistant soit monté, et un `useQuery` ne s'y lit pas. Ce
        // pont est monté dans la coque, donc il voit la confirmation où que
        // soit l'avocat — l'écran assistant, lui, ne verrait que le fil affiché.
        publierLeFilConfirme(conversationId);
        // Trigger global-list reconciliation. Do not remove the phantom here:
        // this WS event arrives before the HTTP refetch updates the TanStack
        // cache, so removal would make the new sidebar row disappear briefly.
        // useGlobalConversationList removes it only after observing this UUID.
        void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      },
    );

    // ----- liste des dossiers de la conversation -----------------------------
    // Ici, et pas dans l'écran : la trame d'une conversation en arrière-plan doit
    // aussi être gardée, et l'avocat averti de qui a perdu la conversation.
    const unsubConversationCases = pool.events.on(
      'conversation_cases_changed',
      (_sessionKey, payload) => appliquerLaListeDeLaConversation(queryClient, payload),
    );

    // ----- memory_usage_updated --------------------------------------------
    const unsubMemory = pool.events.on(
      'memory_usage_updated',
      (sessionKey, usage) => {
        setSession(sessionKey, { memoryUsage: usage });
      },
    );

    // ----- compaction manuelle ---------------------------------------------
    const unsubCompactCapability = pool.events.on(
      'compact_context_capability_changed',
      (sessionKey, compactContextCapability) => {
        setSession(sessionKey, { compactContextCapability });
      },
    );
    const unsubCompaction = pool.events.on(
      'context_compaction_changed',
      (sessionKey, _conversationId, state) => {
        // Jauge lue AVANT tout rafraîchissement : le backend envoie la mesure
        // d'après-compaction juste après `completed`, avec le même seuil.
        const gauge = useActiveSessionsStore.getState().sessions[sessionKey]?.memoryUsage ?? null;
        setSession(sessionKey, { compaction: state });
        notifyContextCompactionOutcome(state, gauge);
      },
    );
    // L'historique persisté a changé hors tour (compaction aboutie, ou issue
    // perdue avec la socket) : seule la base peut montrer les messages archivés
    // et le marqueur de compaction.
    const unsubHistoryStale = pool.events.on(
      'conversation_history_stale',
      (_sessionKey, conversationId) => {
        handleConversationHistoryStale(conversationId, queryClient);
      },
    );

    // ----- session_removed -------------------------------------------------
    // Mirror pool teardown into the store so no stale (dead) key survives — the
    // tombstone otherwise wedges a red dot in the sidebar and gets reattached by
    // AgentV3View (rationale at the emit site, WebSocketPool.cleanup).
    // removeSession/removeConnection no-op if the key is already gone.
    const unsubRemoved = pool.events.on(
      'session_removed',
      (sessionKey) => {
        const store = useActiveSessionsStore.getState();
        // Read the conversation before removing the session mirror: evicted slots
        // keep the local transcript visible but stale, then only active history
        // observers may refresh from REST.
        handleSessionRemoved(sessionKey, queryClient);
        store.removeSession(sessionKey);
        store.removeConnection(sessionKey);
      },
    );

    // ----- turn_settled ----------------------------------------------------
    // A turn that ran out of reach of this socket (other backend instance, or
    // replay window over) is fully persisted: drop the partial local turn and
    // reload the history from the API (order enforced at the definition site).
    const unsubTurnSettled = pool.events.on(
      'turn_settled',
      (_sessionKey, conversationId, localTurnId) => {
        handleTurnSettled(conversationId, localTurnId, queryClient);
      },
    );

    // ----- case_documents_mutated ------------------------------------------
    // WHY: mutating pièce/folder agent tools (set_piece_numbers renumber,
    // create_case_folders, move_pieces, rename_pieces, rename_folders and
    // save_document) change server-side state.
    // The lawyer's pièces panel (case-documents / folder-contents / folder-tree)
    // holds stale piece_number / folder / label values until invalidated. We
    // invalidate both the caseId-scoped keys (immediate refresh of the open case)
    // and the general keys (per convention: "invalidate both specific AND general").
    //
    // `bordereau-docs` (2026-07-17) feeds the open « Tamponner mes pièces » panel.
    // It MUST be here: the bordereau wizard seeds its numbers once at openPanel
    // and never revalidates on its own, so without this key the panel keeps a
    // stale snapshot and writes it back over the agent at generation time —
    // silently, and dragging case_document_embeddings with it. Prefix-only
    // (no droitPublic 3rd element) so both scope variants are reached.
    const unsubDocsMutated = pool.events.on(
      'case_documents_mutated',
      (_sessionKey, caseId) => {
        invalidateCaseDocumentQueries(queryClient, caseId);
      },
    );

    // ----- cases_mutated ----------------------------------------------------
    // L'agent a créé un dossier, ou repris un dossier existant (`create_case`) :
    // la page Dossiers et le sélecteur du composeur doivent le montrer sans
    // rechargement.
    const unsubCasesMutated = pool.events.on('cases_mutated', () => {
      invalidateCaseListQueries(queryClient, cabinetId);
    });

    // ----- quick_actions_mutated ------------------------------------------
    // WHY: agent mutations and manual UI mutations share the same REST-backed
    // personal library. Invalidate its stable Case Discovery key so an open grid
    // reflects a successful manage_quick_actions call without a page reload.
    const unsubQuickActionsMutated = pool.events.on(
      'quick_actions_mutated',
      () => {
        void queryClient.invalidateQueries({ queryKey: USER_PROMPTS_QUERY_KEY });
        void queryClient.invalidateQueries({ queryKey: ['user-prompts'] });
      },
    );

    // ----- skills_mutated ---------------------------------------------------
    // The result frame, not the outgoing consent, is the write receipt. Refresh
    // every mounted skills surface once without replaying the mutation itself.
    const unsubSkillsMutated = pool.events.on('skills_mutated', () => {
      void queryClient.invalidateQueries({ queryKey: skillsQueryKeys.settings(userId) });
      void queryClient.invalidateQueries({ queryKey: skillsQueryKeys.catalog(userId) });
      void queryClient.invalidateQueries({ queryKey: SKILLS_QUERY_KEY });
    });

    // Cleanup: unsubscribe all listeners when the provider unmounts or the
    // pool reference changes.
    return () => {
      unsubTimeline();
      unsubStatus();
      unsubConnection();
      unsubDone();
      unsubConversation();
      unsubConversationCases();
      unsubMemory();
      unsubCompactCapability();
      unsubCompaction();
      unsubHistoryStale();
      unsubRemoved();
      unsubTurnSettled();
      unsubDocsMutated();
      unsubCasesMutated();
      unsubQuickActionsMutated();
      unsubSkillsMutated();
    };
  }, [
    pool,
    queryClient,
    setTimeline,
    setSession,
    setConnection,
    confirmConversationId,
    userId,
    cabinetId,
  ]);
}

// ============================================================================
// Provider
// ============================================================================

interface WebSocketPoolProviderProps {
  children: ReactNode;
}

/**
 * Provides the `WebSocketPool` singleton to the component tree and wires the
 * `SessionBridge` that mirrors pool events into Zustand + TanStack Query.
 *
 * Mount this once near the root of the authenticated app shell (e.g. inside
 * `DashboardLayout`).
 *
 * Unmounting it closes NOTHING: the pool is a module-level singleton and this
 * provider deliberately skips `closeAll()` — see the comment in the body for the
 * three mechanisms that actually reclaim connections. (This docstring used to
 * claim the opposite and sent a 2026-08-09 exploration down the wrong path.)
 */
export function WebSocketPoolProvider({ children }: WebSocketPoolProviderProps): React.JSX.Element {
  // Singleton: `WebSocketPool.getInstance()` returns the same instance across
  // renders; useMemo ensures we don't call it on every render.
  const pool = useMemo(() => WebSocketPool.getInstance(), []);

  // Wire the bridge — must be called before any children render so that
  // events emitted synchronously during session creation are captured.
  useSessionBridge(pool);

  // WHY no closeAll on unmount: the pool is a module-level singleton that survives
  // across route changes. Since 2026-06-21, DashboardLayout is mounted ONCE as a
  // persistent layout route (App.tsx, `<Route element={<DashboardLayout/>}>` →
  // `<Outlet/>`), so this provider no longer unmounts/remounts on every page
  // navigation — the SessionBridge subscriptions stay stable across navs.
  // The provider still only unmounts when the whole authenticated shell tears
  // down (logout / backend-unavailable gate), and even then closeAll() is
  // intentionally NOT called here: connection cleanup is owned by the pool/backend
  // via (1) backend inactivity timeout (30 min), (2) heartbeat timeout (45s),
  // (3) explicit session close or slot eviction (MAX_ACTIVE_SLOTS = 3).

  return (
    <WebSocketPoolContext.Provider value={pool}>
      {children}
    </WebSocketPoolContext.Provider>
  );
}
